(function () {
  'use strict';

  class OrdessaStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ord-sticky-submit]');
      this.quantityLabel = this.querySelector('[data-ord-sticky-quantity]');
      this.subtotalLabel = this.querySelector('[data-ord-sticky-subtotal]');
      this.variantLabel = this.querySelector('[data-ord-sticky-variant]');
      if (!this.button) return;

      try {
        this.currentVariant = JSON.parse(this.querySelector('[data-ord-sticky-initial-variant]')?.textContent || 'null');
      } catch (_) {
        this.currentVariant = null;
      }
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.onViewportChange = this.scheduleUpdate.bind(this);
      this.onSubmitClick = this.onClick.bind(this);
      this.onFieldChange = (event) => {
        if (event.target.closest('product-info') === this.primaryRoot) this.scheduleUpdate();
      };
      this.button.addEventListener('click', this.onSubmitClick);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewportChange, { passive: true });
      this.mobileQuery.addEventListener('change', this.onViewportChange);
      document.addEventListener('input', this.onFieldChange);
      document.addEventListener('change', this.onFieldChange);
      document.addEventListener('focusin', this.onViewportChange);
      document.addEventListener('focusout', this.onViewportChange);
      this.stateObserver = new MutationObserver((records) => {
        if (records.some((record) => {
          const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return target && !this.contains(target) && (
            this.primaryRoot?.contains(target) || target === document.body ||
            target.closest('cart-drawer, cart-notification, .drawer, dialog, [role="dialog"], product-modal, modal-dialog, #ordessa-welcome-root, details.menu-opening')
          );
        })) this.scheduleUpdate();
      });
      this.stateObserver.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-busy', 'hidden', 'open', 'aria-hidden', 'style', 'value']
      });
      this.bindSubscriptions();
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onSubmitClick);
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      this.mobileQuery?.removeEventListener('change', this.onViewportChange);
      document.removeEventListener('input', this.onFieldChange);
      document.removeEventListener('change', this.onFieldChange);
      document.removeEventListener('focusin', this.onViewportChange);
      document.removeEventListener('focusout', this.onViewportChange);
      this.stateObserver?.disconnect();
      this.unsubscribeVariant?.();
      this.unsubscribeQuantity?.();
      this.unsubscribeCartError?.();
      if (this.cartFocusTimeout) window.clearTimeout(this.cartFocusTimeout);
      this.unsubscribeVariant = null;
      this.unsubscribeQuantity = null;
      this.unsubscribeCartError = null;
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
    }

    bindSubscriptions() {
      if (this.unsubscribeVariant || typeof subscribe !== 'function') return;
      this.unsubscribeVariant = subscribe('variant-change', (event) => {
        if (String(event?.data?.sectionId) !== this.primaryRoot?.dataset.section) return;
        this.currentVariant = event.data.variant || null;
        this.scheduleUpdate();
      });
      this.unsubscribeQuantity = subscribe('quantity-update', () => this.scheduleUpdate());
      this.unsubscribeCartError = subscribe('cart-error', (event) => {
        const variantId = this.primaryRoot?.querySelector('product-form [name="id"]')?.value;
        if (event?.source !== 'product-form' || String(event.productVariantId) !== variantId) return;
        this.pendingCartFocus = false;
        window.clearTimeout(this.cartFocusTimeout);
      });
    }

    scheduleUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        if (this.isConnected) this.update();
      });
    }

    isRendered(element) {
      if (!element?.isConnected || !element.getClientRects().length) return false;
      for (let node = element; node; node = node.parentElement) {
        if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        if (node.tagName === 'DETAILS' && !node.open) {
          const summary = node.querySelector(':scope > summary');
          if (!summary?.contains(element)) return false;
        }
      }
      return true;
    }

    dialogOpen() {
      return [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], cart-drawer.active, cart-notification.active, .cart-notification.active, .drawer.active, details[open].menu-opening')]
        .some((element) => !this.contains(element) && this.isRendered(element));
    }

    setText(element, text) {
      if (element && element.textContent !== text) element.textContent = text;
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: this.dataset.currency || 'AUD'
      }).format(cents / 100);
    }

    update() {
      this.primaryRoot = [...document.querySelectorAll('product-info[id^="MainProduct-"]')]
        .find((element) => element.dataset.productId === this.dataset.productId && this.isRendered(element));
      const form = this.primaryRoot?.querySelector('product-form form[data-type="add-to-cart-form"]');
      this.primaryButton = form?.querySelector('[type="submit"]');
      if (!form || !this.primaryButton) {
        this.setVisibility(false, this.dialogOpen());
        return;
      }

      const variantId = form.querySelector('[name="id"]')?.value;
      if (String(this.currentVariant?.id) !== variantId) {
        try {
          const selected = JSON.parse(this.primaryRoot.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
          this.currentVariant = String(selected?.id) === variantId ? selected : null;
        } catch (_) {
          this.currentVariant = null;
        }
      }
      const quantityInput = form.elements.namedItem('quantity');
      const quantity = quantityInput ? Number(quantityInput.value) : 1;
      const validQuantity = Number.isInteger(quantity) && quantity > 0 && (quantityInput?.validity?.valid ?? true);
      const validPrice = typeof this.currentVariant?.price === 'number' && Number.isFinite(this.currentVariant.price);
      this.setText(this.quantityLabel, validQuantity ? String(quantity) : '—');
      this.setText(this.subtotalLabel, validQuantity && validPrice ? this.money(this.currentVariant.price * quantity) : '—');
      const variantTitle = this.currentVariant?.title === 'Default Title' ? '' : (this.currentVariant?.title || '');
      this.setText(this.variantLabel, variantTitle);
      if (this.variantLabel) this.variantLabel.hidden = !variantTitle;

      const busy = this.primaryButton.classList.contains('loading') || this.primaryButton.getAttribute('aria-busy') === 'true';
      const disabled = this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true' || busy;
      this.button.disabled = disabled;
      this.button.setAttribute('aria-disabled', String(disabled));
      this.button.setAttribute('aria-busy', String(busy));
      const nativeLabel = this.primaryButton.innerText.replace(/\s+/g, ' ').trim();
      this.setText(this.button, busy ? this.dataset.labelAdding : (disabled ? nativeLabel : this.button.dataset.labelDefault));
      const popup = this.primaryButton.getAttribute('aria-haspopup');
      if (popup) this.button.setAttribute('aria-haspopup', popup);
      else this.button.removeAttribute('aria-haspopup');

      const dialogOpen = this.dialogOpen();
      const cartOpen = [...document.querySelectorAll('cart-drawer.active, cart-notification.active, .cart-notification.active')]
        .some((element) => this.isRendered(element));
      if (this.pendingCartFocus && cartOpen) {
        this.returnAfterCart = true;
        this.pendingCartFocus = false;
        window.clearTimeout(this.cartFocusTimeout);
      }
      const bounds = this.primaryButton.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const viewportTop = window.visualViewport?.offsetTop || 0;
      const mainButtonVisible = this.isRendered(this.primaryButton) && bounds.bottom > viewportTop && bounds.top < viewportTop + viewportHeight;
      const title = this.primaryRoot.querySelector('.product__title');
      const price = this.primaryRoot.querySelector('[id^="price-"]');
      const identityEnd = Math.max(title?.getBoundingClientRect().bottom || Infinity, price?.getBoundingClientRect().bottom || Infinity);
      const identityPassed = identityEnd <= viewportTop;
      const browsingStarted = identityPassed || (window.scrollY >= 240 && (title?.getBoundingClientRect().bottom || Infinity) <= viewportTop);
      const fieldFocused = document.activeElement?.matches('input, select, textarea');
      this.setVisibility(this.mobileQuery.matches && browsingStarted && !mainButtonVisible && !dialogOpen && !fieldFocused, dialogOpen);
      if (this.returnAfterCart && !cartOpen && !dialogOpen) {
        this.returnAfterCart = false;
        if (!this.hidden && !this.button.disabled) this.button.focus({ preventScroll: true });
        else if (!this.primaryButton.disabled) this.primaryButton.focus();
        else this.primaryRoot.querySelector('h1')?.focus();
      }
    }

    setVisibility(visible, dialogOpen) {
      if (!visible && !this.hidden && this.contains(document.activeElement) && !dialogOpen) {
        const target = !this.primaryButton?.disabled ? this.primaryButton : this.primaryRoot?.querySelector('h1');
        target?.focus();
      }
      this.hidden = !visible;
      this.inert = !visible;
      this.setAttribute('aria-hidden', String(!visible));
      this.classList.toggle('is-visible', visible);
    }

    onClick(event) {
      event.preventDefault();
      this.update();
      if (this.hidden || this.button.disabled || !this.primaryButton) return;
      this.pendingCartFocus = true;
      this.primaryButton.click();
      if (!this.primaryButton.classList.contains('loading') && this.primaryButton.getAttribute('aria-busy') !== 'true') {
        this.pendingCartFocus = false;
      } else {
        window.clearTimeout(this.cartFocusTimeout);
        this.cartFocusTimeout = window.setTimeout(() => { this.pendingCartFocus = false; }, 30000);
      }
      this.scheduleUpdate();
    }
  }

  if (!customElements.get('ordessa-sticky-atc')) {
    customElements.define('ordessa-sticky-atc', OrdessaStickyAtc);
  }

  /* ------------------------------------------------------------------
     Recently viewed products (privacy-light: handles only, localStorage,
     capped, and never sent anywhere).
     ------------------------------------------------------------------ */
  const RECENT_KEY = 'ordessa:recently-viewed';
  const RECENT_MAX = 8;

  function recordRecentlyViewed() {
    const el = document.querySelector('[data-ord-record-view]');
    if (!el) return;

    const handle = el.dataset.ordRecordView;
    if (!handle) return;

    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const next = [handle].concat(stored.filter((h) => h !== handle)).slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      /* Storage unavailable (private mode, quota) — feature simply skipped. */
    }
  }

  /* ------------------------------------------------------------------
     Announce cart updates to assistive technology.
     Craft updates the cart via fetch; without a live region, screen reader
     users get no confirmation that anything happened.
     ------------------------------------------------------------------ */
  function bindCartAnnouncements() {
    const region = document.getElementById('ordessa-cart-live-region');
    if (!region || typeof subscribe !== 'function') return;

    subscribe('cart-update', () => {
      region.textContent = region.dataset.messageUpdated || 'Cart updated.';
      window.setTimeout(() => { region.textContent = ''; }, 3000);
    });
  }

  /**
   * Keep aria-expanded on FAQ <summary> elements in step with the real open state.
   *
   * A native <details>/<summary> already exposes its expanded state to assistive
   * technology, so this is belt-and-braces rather than a fix for a defect. It is
   * driven by the element's `toggle` event precisely because a hard-coded
   * aria-expanded would be a genuine accessibility bug the moment the user opened
   * the accordion — the attribute would then contradict what is on screen.
   *
   * aria-controls is rendered server-side in ordessa-faq.liquid.
   */
  function bindFaqAccordions() {
    const items = document.querySelectorAll('.ord-faq__item');
    if (!items.length) return;

    items.forEach((item) => {
      const summary = item.querySelector('.ord-faq__summary');
      if (!summary) return;

      const sync = () => summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');

      sync();
      item.addEventListener('toggle', sync);
    });
  }

  function init() {
    document.querySelectorAll('ordessa-sticky-atc').forEach((bar) => bar.bindSubscriptions());
    recordRecentlyViewed();
    bindCartAnnouncements();
    bindFaqAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

